import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, CircularProgress, Tooltip, Snackbar, Alert,
    InputAdornment, Chip, Pagination, Autocomplete
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort'; // Para posible ordenamiento futuro
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'; // Para posible ordenamiento futuro
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'; // Para posible ordenamiento futuro
import WebhookIcon from '@mui/icons-material/Webhook'; // <-- ÍCONO AÑADIDO


// --- TIPOS E INTERFACES ---
interface PostbackLogRow {
    id: number;
    conversion_id: number;
    request_url: string;
    response_status_code: number;
    response_body: string;
    timestamp: string;
    traffic_source_name: string;
}
interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    pageSize: number;
}
interface FilterOption { id: number; name: string; }

// Define el tipo de rango de fecha para mayor claridad (reutilizado de ConversionsLogPage)
type DateRangeOption = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month' | 'last_month' | 'custom';

// Helper function to calculate dates based on range selection (reutilizado de ConversionsLogPage)
const getDateRange = (range: DateRangeOption) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    let start = new Date(today);
    let end = new Date(today);

    switch (range) {
        case 'today':
            // start and end are already today
            break;
        case 'yesterday':
            start.setDate(today.getDate() - 1);
            end.setDate(today.getDate() - 1);
            break;
        case 'last_7_days':
            start.setDate(today.getDate() - 6);
            break;
        case 'last_30_days':
            start.setDate(today.getDate() - 29);
            break;
        case 'this_month':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
            break;
        case 'last_month':
            start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            end = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
            break;
        // 'custom' case will be handled by direct TextField input, so no change here
    }
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
};

// --- HELPER COMPONENTS ---

/**
 * PostbackLogFilterForm Component
 * Renders the filter form for postback log.
 */
const PostbackLogFilterForm: React.FC<{
    startDate: string;
    setStartDate: React.Dispatch<React.SetStateAction<string>>;
    endDate: string;
    setEndDate: React.Dispatch<React.SetStateAction<string>>;
    selectedTsId: number | '';
    setSelectedTsId: React.Dispatch<React.SetStateAction<number | ''>>;
    trafficSources: FilterOption[];
    handleFilterSubmit: (event: React.FormEvent) => void;
    isLoading: boolean;
    selectedDateRange: DateRangeOption;
    setSelectedDateRange: React.Dispatch<React.SetStateAction<DateRangeOption>>;
}> = ({
    startDate, setStartDate, endDate, setEndDate,
    selectedTsId, setSelectedTsId, trafficSources,
    handleFilterSubmit, isLoading,
    selectedDateRange, setSelectedDateRange
}) => {
    return (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                Filtros del Log de Postbacks
            </Typography>
            <Box component="form" onSubmit={handleFilterSubmit} noValidate>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-end">
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Rango de Fecha</InputLabel>
                        <Select
                            value={selectedDateRange}
                            onChange={(e) => {
                                const newRange = e.target.value as DateRangeOption;
                                setSelectedDateRange(newRange);
                                if (newRange !== 'custom') {
                                    const { startDate: newStart, endDate: newEnd } = getDateRange(newRange);
                                    setStartDate(newStart);
                                    setEndDate(newEnd);
                                }
                            }}
                            label="Rango de Fecha"
                        >
                            <MenuItem value="today">Hoy</MenuItem>
                            <MenuItem value="yesterday">Ayer</MenuItem>
                            <MenuItem value="last_7_days">Últimos 7 Días</MenuItem>
                            <MenuItem value="last_30_days">Últimos 30 Días</MenuItem>
                            <MenuItem value="this_month">Este Mes</MenuItem>
                            <MenuItem value="last_month">Mes Anterior</MenuItem>
                            <MenuItem value="custom">Personalizado</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Desde"
                        type="date"
                        value={startDate}
                        onChange={e => {
                            setStartDate(e.target.value);
                            setSelectedDateRange('custom');
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        disabled={selectedDateRange !== 'custom'}
                    />
                    <TextField
                        fullWidth
                        label="Hasta"
                        type="date"
                        value={endDate}
                        onChange={e => {
                            setEndDate(e.target.value);
                            setSelectedDateRange('custom');
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        disabled={selectedDateRange !== 'custom'}
                    />
                    {/* Autocomplete para Fuentes de Tráfico */}
                    <Autocomplete
                        fullWidth
                        options={trafficSources}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={trafficSources.find(ts => ts.id === selectedTsId) || null}
                        onChange={(event, newValue) => {
                            setSelectedTsId(newValue ? newValue.id : '');
                        }}
                        renderInput={(params) => <TextField {...params} label="Fuente de Tráfico" variant="outlined" />}
                        filterOptions={(options, { inputValue }) => {
                            const lowercasedInput = inputValue.toLowerCase();
                            return options.filter(option =>
                                option.name.toLowerCase().includes(lowercasedInput) ||
                                String(option.id).includes(lowercasedInput)
                            );
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ height: '56px', minWidth: '150px' }}
                        disabled={isLoading}
                        startIcon={<SearchIcon />}
                    >
                        {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
};

/**
 * PostbackLogTable Component
 * Renders the table of postback logs with search and pagination.
 */
const PostbackLogTable: React.FC<{
    logs: PostbackLogRow[];
    pagination: PaginationInfo | null;
    isLoading: boolean;
    error: string | null;
    handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
}> = ({ logs, pagination, isLoading, error, handlePageChange }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px', mt: 4 }}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" component="h2" gutterBottom color="text.primary">
                    Registros de Postbacks
                </Typography>
                {isLoading && logs.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Cargando logs...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ m: 2 }}>Error: {error}</Alert>
                ) : logs.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No se encontraron registros de postbacks para esta selección.</Typography>
                ) : (
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellStyle}>Fecha</TableCell>
                                    <TableCell sx={headerCellStyle}>Fuente de Tráfico</TableCell>
                                    <TableCell sx={headerCellStyle}>Conversion ID</TableCell>
                                    <TableCell sx={headerCellStyle}>URL Enviada</TableCell>
                                    <TableCell sx={headerCellStyle}>Estado HTTP</TableCell>
                                    <TableCell sx={headerCellStyle}>Respuesta</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map(log => (
                                    <TableRow key={log.id} hover>
                                        <TableCell>{new Date(log.timestamp).toLocaleString('es-MX')}</TableCell>
                                        <TableCell>{log.traffic_source_name}</TableCell>
                                        <TableCell>{log.conversion_id}</TableCell>
                                        <TableCell sx={{ maxWidth: '300px', overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                                            <Tooltip title={log.request_url}>
                                                <span>{log.request_url.length > 50 ? `${log.request_url.substring(0, 47)}...` : log.request_url}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={log.response_status_code}
                                                color={log.response_status_code >= 200 && log.response_status_code < 300 ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: '300px', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                            <Tooltip title={log.response_body}>
                                                <span>{log.response_body.length > 50 ? `${log.response_body.substring(0, 47)}...` : log.response_body}</span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                
                {pagination && logs.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                            disabled={isLoading}
                            sx={{ mb: { xs: 2, sm: 0 }, mr: { sm: 2 } }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Página {pagination.currentPage} de {pagination.totalPages} (Total: {pagination.totalRecords} registros)
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};


const PostbackLogPage: React.FC = () => {
    // --- ESTADOS ---
    const [logs, setLogs] = useState<PostbackLogRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null); // Notificaciones unificadas

    // Estados para los filtros y paginación
    const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>('today');
    const initialDates = getDateRange(selectedDateRange);
    const [startDate, setStartDate] = useState<string>(initialDates.startDate);
    const [endDate, setEndDate] = useState<string>(initialDates.endDate);
    const [selectedTsId, setSelectedTsId] = useState<number | ''>('');
    const [currentPage, setCurrentPage] = useState(1);
    
    const [trafficSources, setTrafficSources] = useState<FilterOption[]>([]);

    // --- LÓGICA DE DATOS ---
    // Carga las opciones para las fuentes de tráfico una sola vez
    useEffect(() => {
        axios.get(`${API_BASE_URL}/traffic-sources`)
            .then(res => setTrafficSources(res.data))
            .catch(e => {
                console.error("Error cargando fuentes de tráfico", e);
                setNotification({ type: 'error', message: e.response?.data?.message || "Error al cargar fuentes de tráfico." });
            });
    }, []);

    // Hook para recalcular startDate y endDate cuando cambia selectedDateRange
    useEffect(() => {
        if (selectedDateRange !== 'custom') {
            const { startDate: newStart, endDate: newEnd } = getDateRange(selectedDateRange);
            setStartDate(newStart);
            setEndDate(newEnd);
        }
    }, [selectedDateRange]);


    const fetchLogs = useCallback(() => {
        setIsLoading(true);
        setError(null);

        const apiEndDate = new Date(endDate);
        apiEndDate.setDate(apiEndDate.getDate() + 1); // Asegurar que el endDate incluya todo el día
        let url = `${API_BASE_URL}/postback-logs?page=${currentPage}&limit=50&startDate=${startDate}&endDate=${apiEndDate.toISOString().split('T')[0]}`;
        if (selectedTsId) url += `&traffic_source_id=${selectedTsId}`;

        axios.get(url)
            .then(response => {
                setLogs(response.data.data);
                setPagination(response.data.pagination);
            })
            .catch(err => {
                const errorMessage = err.response ? err.response.data.message : "Error de red o CORS.";
                setError(errorMessage);
                setNotification({ type: 'error', message: errorMessage });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [startDate, endDate, selectedTsId, currentPage]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // --- MANEJADORES DE EVENTOS ---
    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1); // Al aplicar filtros, volvemos a la página 1, lo que dispara el useEffect
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    // --- RENDERIZADO ---
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Snackbar para notificaciones */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <WebhookIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Log de Postbacks Enviados
            </Typography>
            
            {/* Formulario de filtros */}
            <PostbackLogFilterForm
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedTsId={selectedTsId}
                setSelectedTsId={setSelectedTsId}
                trafficSources={trafficSources}
                handleFilterSubmit={handleFilterSubmit}
                isLoading={isLoading}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
            />

            {/* Tabla de logs de postbacks */}
            <PostbackLogTable
                logs={logs}
                pagination={pagination}
                isLoading={isLoading}
                error={error}
                handlePageChange={handlePageChange}
            />
        </Container>
    );
}

export default PostbackLogPage;